/**
 * EvaluatorIntentPanel
 *
 * Displays what the evaluator asked for when they tossed the ball down.
 * This is the Worker's primary frame of reference — everything they do
 * should be in response to this intent.
 *
 * Shown only when the ball is in the Worker's possession.
 *
 * Source: loop.intent (jsonb on catchball_loops)
 * Structure: { strategicContext, asks, constraints, guardrails, responseGuidance, clarifications, dueDate }
 */

import { AlertCircle, Calendar, HelpCircle, Shield, Target } from 'lucide-react';
import type { LoopIntent } from '../../../types/prioritization/catchballLoop';
import { formatCalendarDate } from '../../../lib/calendarDate';

interface EvaluatorIntentPanelProps {
  intent: LoopIntent;
  /** Cycle owner / executive who issued the request (shown as “Request from …”). */
  requestFromLabel?: string;
  evaluatorName?: string;
  evaluatorLevel?: string;
  tossedAt?: string;
}

export function EvaluatorIntentPanel({
  intent,
  requestFromLabel,
  evaluatorName,
  evaluatorLevel,
  tossedAt,
}: EvaluatorIntentPanelProps) {
  const hasConstraints = (intent.constraints ?? []).length > 0;
  const hasGuardrails = (intent.guardrails ?? []).length > 0;
  const hasAsks = (intent.asks ?? []).length > 0;
  const hasClarifications =
    (intent.clarifications ?? []).filter((c) => c.question.trim()).length > 0;

  return (
    <section className="border border-[var(--slate)] dark:border-[var(--slate)]/50 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 p-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          {requestFromLabel && (
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-sec)] mb-1">
              Request from {requestFromLabel}
            </p>
          )}
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-sec)] dark:text-[var(--text-ter)]/90">
            What you've been asked to do
          </p>
          <p className="mt-1 text-xs font-medium text-[var(--text-sec)]">Strategic context</p>
          <h2 className="mt-0.5 text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            {intent.strategicContext || 'No strategic context set'}
          </h2>
        </div>
        {tossedAt && (
          <div className="flex items-center gap-1.5 text-xs text-[var(--text-sec)]">
            <Calendar className="h-3.5 w-3.5" />
            <span>
              Received{' '}
              {new Date(tossedAt).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          </div>
        )}
      </div>

      {/* From line — only when distinct from requestFromLabel (avoid duplicate “From”). */}
      {(evaluatorName || evaluatorLevel) &&
        !(requestFromLabel && evaluatorName && requestFromLabel === evaluatorName) && (
        <p className="mt-2 text-xs text-[var(--text-sec)]">
          From{' '}
          <span className="font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            {evaluatorName ?? evaluatorLevel}
          </span>
          {evaluatorName && evaluatorLevel && (
            <span className="text-[var(--text-sec)]"> · {evaluatorLevel}</span>
          )}
        </p>
      )}

      <div className="mt-4 space-y-4">
        {/* Asks / Goals */}
        {hasAsks && (
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Target className="h-4 w-4 text-[var(--text-ter)]" />
              <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-sec)]">
                Goals / asks
              </span>
            </div>
            <ul className="space-y-1">
              {intent.asks.map((ask, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[var(--text-pri)]">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[var(--slate)] shrink-0" />
                  {ask}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Constraints */}
        {hasConstraints && (
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <AlertCircle className="h-4 w-4 text-[var(--coral)]" />
              <span className="text-xs font-semibold uppercase tracking-wide text-[var(--coral)]">
                Constraints
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {intent.constraints.map((c, i) => (
                <span
                  key={i}
                  className="inline-flex items-center rounded-full bg-[var(--coral-dim)] px-3 py-1 text-xs font-medium text-[var(--coral)]"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Guardrails */}
        {hasGuardrails && (
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Shield className="h-4 w-4 text-[var(--mustard)]" />
              <span className="text-xs font-semibold uppercase tracking-wide text-[var(--mustard)]">
                Guardrails
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {intent.guardrails!.map((g, i) => (
                <span
                  key={i}
                  className="inline-flex items-center rounded-full bg-[var(--mustard-dim)] px-3 py-1 text-xs font-medium text-[var(--mustard)]"
                >
                  {g}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Response guidance */}
        {intent.responseGuidance && (
          <div className="bg-[var(--bg-raised)] border border-[var(--slate)] px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1">
              What a good response looks like
            </p>
            <p className="text-sm text-[var(--text-sec)]">{intent.responseGuidance}</p>
          </div>
        )}

        {/* Open questions / clarifications from evaluator */}
        {hasClarifications && (
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <HelpCircle className="h-4 w-4 text-[var(--text-ter)]" />
              <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-sec)]">
                Open questions from evaluator
              </span>
            </div>
            <div className="space-y-2">
              {intent.clarifications!
                .filter((c) => c.question.trim())
                .map((c) => (
                  <div
                    key={c.id}
                    className="bg-[var(--slate-dim)] border border-[var(--slate)] px-3 py-2 text-sm text-[var(--text-sec)]"
                  >
                    {c.question}
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Due date / limits */}
        {(intent.dueDate || intent.maxIterations != null) && (
          <div className="flex flex-wrap gap-4 text-xs text-[var(--text-sec)] pt-1">
            {intent.dueDate && (
              <span>
                <span className="font-medium text-[var(--text-sec)]">Due: </span>
                {formatCalendarDate(intent.dueDate, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            )}
            {intent.maxIterations != null && (
              <span>
                <span className="font-medium text-[var(--text-sec)]">Max iterations: </span>
                {intent.maxIterations}
              </span>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default EvaluatorIntentPanel;
